import React, { useState, useEffect } from 'react';
import { aboutService, AboutContent } from '../services/aboutService';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const About = () => {
  const [aboutContent, setAboutContent] = useState<AboutContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAboutContent = async () => {
      try {
        setIsLoading(true);
        const data = await aboutService.getAboutContent();
        setAboutContent(data);
      } catch (err) {
        console.error('Error loading about content:', err);
        setError('Failed to load page content.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAboutContent();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !aboutContent) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="bg-red-50 p-4 rounded-md text-red-700 max-w-md mx-auto">
          <p>{error || 'Unable to load page content.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-16">
        <h1 className="text-4xl font-display font-bold text-primary-900 mb-8">
          About Ridgeline Homes
        </h1>
        
        <div className={`flex flex-col ${aboutContent.profileImage ? 'lg:flex-row' : ''} gap-12 mb-12`}>
          <div className={`prose prose-lg max-w-none ${aboutContent.profileImage ? 'lg:w-1/2' : 'w-full'}`}>
            <p className="text-primary-600 mb-6">
              {aboutContent.companyDescription}
            </p>
            <p className="text-primary-600 mb-6">
              {aboutContent.teamDescription}
            </p>
          </div>
          
          {aboutContent.profileImage && (
            <div className="lg:w-1/2 flex items-start">
              <div className="w-full h-auto rounded-lg shadow-lg overflow-hidden">
                <img 
                  src={aboutContent.profileImage} 
                  alt="Ridgeline Homes Team" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-display font-bold text-primary-900 mt-8 mb-4">
            {aboutContent.valuesTitle}
          </h2>
          <ul className="list-disc ml-6 text-primary-600 mb-6 space-y-2">
            {aboutContent.valuesList.map((value, index) => (
              <li key={index} className="pl-2">{value}</li>
            ))}
          </ul>
          
          <h2 className="text-2xl font-display font-bold text-primary-900 mt-8 mb-4">
            {aboutContent.processTitle}
          </h2>
          <p className="text-primary-600 mb-6">
            {aboutContent.processDescription}
          </p>
          <ol className="list-decimal ml-6 text-primary-600 mb-6 space-y-2">
            {aboutContent.processList.map((step, index) => (
              <li key={index} className="pl-2">{step}</li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default About; 